<template>
  <VDialogV3
    ref="dialog"
    id="company-card"
    position="right"
    width="600px"

    :entity="localCompany"

    :context-menu="CompaniesService.getContextMenu(partner, readOnly, false)"
    :submit-method="submit"
  >
    <section>
      <VSelect
        ref="company-country"
        :model-value="localCompany.country"
        @update:modelValue="onCountryChanged"
        label="Страна"
        layout="vertical"
        required
        :clearable="false"
        :options="CompaniesService.countries"
        option-identity="name"
        searchable
        :disabled="readOnly"
      />

      <VInput
        ref="company-name-input"
        v-model="localCompany.name"
        label="Название"
        layout="vertical"
        required
        :disabled="readOnly"
      />
    </section>

    <hr>

    <section v-if="hasAdditionalAttributes && localCompany.country">
      <VTextarea
        ref="company-full-name-input"
        v-model="localCompany.additionalAttributes.nameFull.value"
        :label="localCompany.additionalAttributes.nameFull.name"
        layout="vertical"
        auto-height
        required
        :disabled="readOnly"
      >
        <template
          v-if="isRU"
          #helper-text
        >
          Полное наименование юридического лица из выписки ЕГРЮЛ
        </template>
      </VTextarea>

      <RUAdditionalAttributes
        v-if="isRU"
        :company="localCompany"
        :read-only="readOnly"
      />
      <ESAdditionalAttributes
        v-if="isES"
        :company="localCompany"
        :read-only="readOnly"
      />
      <GBAdditionalAttributes
        v-if="isGB"
        :company="localCompany"
        :read-only="readOnly"
      />
      <CHAdditionalAttributes
        v-if="isCH"
        :company="localCompany"
        :read-only="readOnly"
      />
      <PLAdditionalAttributes
        v-if="isPL"
        :company="localCompany"
        :read-only="readOnly"
      />
      <DEAdditionalAttributes
        v-if="isDE"
        :company="localCompany"
        :read-only="readOnly"
      />
      <INAdditionalAttributes
        v-if="isIN"
        :company="localCompany"
        :read-only="readOnly"
      />
      <UZAdditionalAttributes
        v-if="isUZ"
        :company="localCompany"
        :read-only="readOnly"
      />
      <BYAdditionalAttributes
        v-if="isBY"
        :company="localCompany"
        :read-only="readOnly"
      />
      <CNAdditionalAttributes
        v-if="isCN"
        :company="localCompany"
        :read-only="readOnly"
      />
      <KZAdditionalAttributes
        v-if="isKZ"
        :company="localCompany"
        :read-only="readOnly"
      />

      <template v-if="!isRU">
        <VInput
          v-if="localCompany.additionalAttributes?.address"
          ref="company-address-input"
          v-model="localCompany.additionalAttributes.address.value"
          :label="localCompany.additionalAttributes.address.name"
          layout="vertical"
          required
          :disabled="readOnly"
        />
      </template>
    </section>

    <hr>

    <section>
      <VSelectV2
        ref="company-default-store"
        v-model="localCompany.defaultStore"
        label="Склад доставки по умолчанию"
        layout="vertical"
        searchable
        :data="defaultStoreSource"
        :disabled="readOnly"
      >
        <template #helper-text>
          Подставляется в заказ при выборе этого юр. лица грузополучателем
        </template>
      </VSelectV2>

      <VInputNumber
        ref="swFinClientId"
        v-model="localCompany.swFinClientId"
        label="Внешний id"
        layout="vertical"
        placeholder="id компании"
        :disabled="readOnly"
      >
        <template #helper-text>
          id компании в базе ЛСЗ (таблица tehno.finclient)
        </template>
      </VInputNumber>
    </section>
  </VDialogV3>
</template>

<script lang="ts">

import CompaniesService 			from '@/domains/Companies/CompaniesService';
import Company        				from '@/domains/Companies/Company';
import Partner        				from '@/domains/Partners/Partner';
import StoresService          from '@/domains/Stores/StoresService';

import RUAdditionalAttributes from '@/domains/Companies/components/CompanyCard/AdditionalAttributes/RUAdditionalAttributes.vue';
import ESAdditionalAttributes from '@/domains/Companies/components/CompanyCard/AdditionalAttributes/ESAdditionalAttributes.vue';
import GBAdditionalAttributes from '@/domains/Companies/components/CompanyCard/AdditionalAttributes/GBAdditionalAttributes.vue';
import CHAdditionalAttributes from '@/domains/Companies/components/CompanyCard/AdditionalAttributes/CHAdditionalAttributes.vue';
import PLAdditionalAttributes from '@/domains/Companies/components/CompanyCard/AdditionalAttributes/PLAdditionalAttributes.vue';
import DEAdditionalAttributes from '@/domains/Companies/components/CompanyCard/AdditionalAttributes/DEAdditionalAttributes.vue';
import INAdditionalAttributes from '@/domains/Companies/components/CompanyCard/AdditionalAttributes/INAdditionalAttributes.vue';
import UZAdditionalAttributes from '@/domains/Companies/components/CompanyCard/AdditionalAttributes/UZAdditionalAttributes.vue';
import BYAdditionalAttributes from '@/domains/Companies/components/CompanyCard/AdditionalAttributes/BYAdditionalAttributes.vue';
import CNAdditionalAttributes from '@/domains/Companies/components/CompanyCard/AdditionalAttributes/CNAdditionalAttributes.vue';
import KZAdditionalAttributes from '@/domains/Companies/components/CompanyCard/AdditionalAttributes/KZAdditionalAttributes.vue';


export default defineComponent({
  name:   'CompanyCard',

  components: {
    RUAdditionalAttributes,
    ESAdditionalAttributes,
    GBAdditionalAttributes,
    CHAdditionalAttributes,
    PLAdditionalAttributes,
    DEAdditionalAttributes,
    INAdditionalAttributes,
    UZAdditionalAttributes,
    BYAdditionalAttributes,
    CNAdditionalAttributes,
    KZAdditionalAttributes,
  },

  props:  {
    /** null — создание новой компании партнёра */
    company: {
      type: Company,
      default: null,
    },
    partner: {
      type: Partner,
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    }
  },

  setup() {
    return {
      CompaniesService,
    };
  },

  data() {
    /** Склады партнёра, которому принадлежит юр. лицо. */
    const defaultStoreSource = this.$core.loader.select<Company>(({partner}) =>
      StoresService.getList({partnerId: partner.id})
    )
      .dependsOn({partner: () => this.localCompany?.partner ?? null})
      .preloadOnFocus();

    return {
      localCompany: null as Company | null,
      defaultStoreSource,
    };
  },

  async created() {
    await this.init();
  },

  computed: {
    countryCode() {
      return this.localCompany?.country?.code ?? null;
    },

    isRU() {
      return this.countryCode === 'RU';
    },
    isES() {
      return this.countryCode === 'ES';
    },
    isGB() {
      return this.countryCode === 'GB';
    },
    isCH() {
      return this.countryCode === 'CH';
    },
    isPL() {
      return this.countryCode === 'PL';
    },
    isDE() {
      return this.countryCode === 'DE';
    },
    isIN() {
      return this.countryCode === 'IN';
    },
    isUZ() {
      return this.countryCode === 'UZ';
    },
    isBY() {
      return this.countryCode === 'BY';
    },
    isCN() {
      return this.countryCode === 'CN';
    },
    isKZ() {
      return this.countryCode === 'KZ';
    },

    hasAdditionalAttributes() {
      const attrs = this.localCompany?.additionalAttributes;
      return attrs && Object.keys(attrs).length > 0;
    },
  },

  methods: {
    /** Новую компанию собирает карточка: реквизиты зависят от страны партнёра */
    async init(): Promise<void> {
      if (this.company === null) {
        this.localCompany = await Company.fromPartner(this.partner);
      }
      else {
        this.localCompany = this.$core.utils.cloneDeep(this.company);
      }
    },

    /** Сабмит через submitMethod: валидацию формы и resolve() делает VDialogV3 */
    async submit(): Promise<boolean> {
      if (this.localCompany.isExisting === true) {
        return await CompaniesService.update(this.localCompany);
      }
      else {
        const addedCompany = await CompaniesService.add(this.localCompany, this.partner.id);

        return this.$core.utils.isNullOrUndefined(addedCompany) === false;
      }
    },

    /**
     * Смена страны пересоздаёт компанию с нуля: реквизиты, налоговый режим
     * и ставка НДС у каждой страны свои, вычищать их поштучно не нужно.
     * У сохранённой компании переносим идентичность, чтобы сабмит обновил
     * её, а не создал новую.
     */
    async onCountryChanged(country) {
      const company = await Company.fromPartner(this.partner, country);

      company.id      = this.localCompany.id;
      company.version = this.localCompany.version;

      this.localCompany = company;
    },
  }
});
</script>